import { useState } from "react";
import { Footer } from "../components/layout/Footer";
import Navbar from "../components/layout/Navbar";
import { Button } from "../components/Button";
import { Link } from "react-router-dom";
import { Check, X } from "lucide-react";
import { motion } from "framer-motion";
import { useSelector } from "react-redux";
import { selectGlobalData } from "../features/appSlice";
import { DEFAULTS, PRICING } from "../utils/pricing";
import { useMemo } from "react";

const Pricing = () => {
  const [isMonthly, setIsMonthly] = useState(true);
  const { userLocation } = useSelector(selectGlobalData);

  const prices = useMemo(() => {
    const country = userLocation?.country;
    if (country && PRICING[country]) {
      return PRICING[country];
    }
    return DEFAULTS;
  }, [userLocation]);

  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeInOut",
        when: "beforeChildren",
        staggerChildren: 0.2,
      },
    },
    exit: { opacity: 0, y: -20 },
  };

  // Child element variants
  const childVariants = {
    initial: { opacity: 0, y: 30 },
    animate: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "backOut" },
    },
  };

  // Image stack variants
  const imageStackVariants = {
    initial: { opacity: 0 },
    animate: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const imageItemVariants = {
    initial: { opacity: 0, x: -50 },
    animate: { opacity: 1, x: 0 },
  };

  return (
    <div>
      <Navbar />
      <motion.div
        initial="initial"
        animate="animate"
        exit="exit"
        variants={pageVariants}
        className="px-8 pt-12 pb-26 flex items-center justify-center gap-14 flex-col landing-bg bg-cover bg-no-repeat bg-center"
      >
        <div className="flex flex-col justify-center items-center gap-10">
          <div className="flex items-center justify-center flex-col text-center gap-2">
            <motion.h1
              variants={childVariants}
              className="font-bold leading-9 xl:leading-13"
            >
              Ready to get started <br /> with JelloSite?
            </motion.h1>
            <motion.p variants={childVariants} className="text-[#907BB4]">
              Access these features when you get this pricing package for <br />
              your business.
            </motion.p>
          </div>
          <motion.div initial="initial" animate="animate" exit="exit">
            <ToggleButton isMonthly={isMonthly} setIsMonthly={setIsMonthly} />
          </motion.div>
        </div>

        <motion.div className="flex flex-wrap justify-center gap-6 items-start">
          {
            isMonthly && 
            <FreeCard 
             symbol={prices.symbol} 
          />
          }
        
        <NormalCard 
           isMonthly={isMonthly} 
           symbol={prices.symbol}
           monthlyPrice={prices.starter_monthly}
           yearlyPrice={prices.starter_yearly}
        />

        </motion.div>
      </motion.div>
      <Footer />
    </div>
  );
};

const ToggleButton = ({
  isMonthly,
  setIsMonthly,
}: {
  isMonthly: boolean;
  setIsMonthly: (val: boolean) => void;
}) => {
  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className="flex items-center gap-3 bg-white"
    >
      <span className="text-sm font-medium text-gray-900">Annually</span>
      <button
        type="button"
        onClick={() => setIsMonthly(!isMonthly)}
        className="relative inline-flex h-7 w-12 items-center rounded-full bg-[#E5EAF1] focus:outline-none"
      >
        <span
          className={`absolute h-5 w-5 rounded-full bg-[#7E57C2] transition-all duration-300 ${
            isMonthly ? "left-[calc(100%-1.50rem)]" : "left-1"
          }`}
        />
      </button>
      <span className="text-sm font-medium text-gray-900">Monthly</span>
    </motion.div>
  );
};

const FreeCard = ({ symbol }: { symbol: string }) => {
  return (
    <>
      <motion.div
        whileHover={{ scale: 1.02 }}
        className="flex items-center justify-center gap-12 flex-col px-3 py-12 w-[300px] sm:w-[375px] border border-[#7E57C233] rounded-2xl bg-white"
      >
        <div className="flex items-center justify-center gap-4 flex-col text-center">
          <p className="text-[#7E57C2] font-medium">Free</p>
          <div>
            <h1 className="font-medium leading-11">{symbol}0</h1>
            <span className="text-[#606571]">
             For 14 days
            </span>
          </div>
          <Link to="/signup">
            <Button text="Start Selling" />
          </Link>
        </div>
        <ul className="flex items-start justify-center flex-col gap-3">
          <li className="flex items-center gap-2">
            <Check size={20} color="#485AFF" />
           Free for 14 days
          </li>
          <li className="flex items-center gap-2">
            <Check size={20} color="#485AFF" />
            Customize store templates
          </li>
          <li className="flex items-center gap-2">
            <Check size={20} color="#485AFF" />
            .jellosite.store domain
          </li>
          <li className="flex items-center gap-2">
            <X size={20} color="#485AFF" />
            Facebook Pixel for Ad tracking
          </li>
          <li className="flex items-center gap-2">
            <X size={20} color="#485AFF" />
            Custom Domain Integration
          </li>
          <li className="flex items-center gap-2">
            <X size={20} color="#485AFF" />
            Custom Payment Integration
          </li>
          {/* <li className="flex items-center gap-2">
            <Check size={20} color="#485AFF" />
            Whatsapp messaging (coming soon)
          </li> */}
        </ul>
      </motion.div>
    </>
  );
};

interface NormalCardProps {
  isMonthly: boolean;
  symbol: string;
  monthlyPrice: number;
  yearlyPrice: number;
}

const NormalCard = ({ isMonthly, symbol, monthlyPrice, yearlyPrice}: NormalCardProps) => {
  const price = isMonthly ? monthlyPrice : yearlyPrice;
  const label = isMonthly ? "Per month" : "Per year";

  return (
    <>
      <motion.div
        whileHover={{ scale: 1.02 }}
        className="flex items-center justify-center gap-12 flex-col px-3 py-12 w-[300px] sm:w-[375px] border border-[#7E57C233] rounded-2xl bg-white"
      >
        <div className="flex items-center justify-center gap-4 flex-col text-center">
          <p className="text-[#7E57C2] font-medium">Starter</p>
          <div>
            <h1 className="font-medium leading-11">
             {symbol}{price.toLocaleString()}
              </h1>
            <span className="text-[#606571] ">{label}</span>
          </div>
          <Link to="/signup">
            <Button text="Starter pack" />
          </Link>
        </div>
        <ul className="flex items-start justify-center flex-col gap-3">
          <li className="flex items-center gap-2">
            <Check size={20} color="#485AFF" />
            Customize store templates
          </li>
          <li className="flex items-center gap-2">
            <Check size={20} color="#485AFF" />
            .jellosite.store domain
          </li>
          <li className="flex items-center gap-2">
            <Check size={20} color="#485AFF" />
            Facebook Pixel for Ad tracking
          </li>
          <li className="flex items-center gap-2">
            <Check size={20} color="#485AFF" />
            Custom Domain Integration (charges apply)
          </li>
          <li className="flex items-center gap-2">
            <Check size={20} color="#485AFF" />
            Custom Payment Integration with Flutterwave and Paystack
          </li>
          {
            !isMonthly &&
      <li className="flex items-center gap-2">
            <Check size={20} color="#485AFF" />
         Includes a free .com.ng domain
          </li>
          }
        </ul>
      </motion.div>
    </>
  );
};

const ComboCard = () => {
  return (
    <>
      <motion.div
        whileHover={{ scale: 1.02 }}
        className="flex items-center justify-center gap-12 flex-col px-3 py-12 w-[300px] sm:w-[375px] border border-[#7E57C233] rounded-2xl bg-white"
      >
        <div className="flex items-center justify-center gap-4 flex-col text-center">
          <p className="text-[#7E57C2] font-medium">Starter Pack</p>
          <div>
            <h1 className="font-medium leading-11">₦15,000</h1>
            <span className="text-[#606571]">For 3 months</span>
          </div>
          <Link to="/signup">
            <Button text="Get Starter Pack" />
          </Link>
        </div>
        <ul className="flex items-start justify-center flex-col gap-3">
          <li className="flex items-center gap-2">
            <Check size={20} color="#485AFF" />
            Customize store templates
          </li>
          <li className="flex items-center gap-2">
            <Check size={20} color="#485AFF" />
            .jellosite.store domain
          </li>
          <li className="flex items-center gap-2">
            <Check size={20} color="#485AFF" />
            Facebook Pixel for Ad tracking
          </li>
          <li className="flex items-center gap-2">
            <Check size={20} color="#485AFF" />
            Custom Domain Integration (charges apply)
          </li>
          <li className="flex items-center gap-2">
            <Check size={20} color="#485AFF" />
            Custom Payment Integration with Flutterwave and Paystack
          </li>
          <li className="flex items-center gap-2">
            <Check size={20} color="#485AFF" />
            Free .com.ng domain name (worth ₦7,000/yr)
          </li>
        </ul>
      </motion.div>
    </>
  );
};

export default Pricing;
